import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  MessageSquare, Send, Loader2, Plus, Trash2, Sparkles, BookOpen, ChevronLeft,
  AlertCircle, Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const newSessionId = () =>
  (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
  `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Suggested starter prompts shown when starting a brand-new chat
const STARTERS = [
  { icon: '🧮', label: 'Explain fractions to me with examples', subject: 'Math' },
  { icon: '🔬', label: 'Why do plants need sunlight?', subject: 'Science' },
  { icon: '📖', label: 'What is the difference between Past and Present Perfect?', subject: 'English' },
  { icon: '🗺️', label: 'Why is the Indus Valley civilisation important?', subject: 'Social' },
  { icon: '🧠', label: 'Help me memorise the multiplication table for 8', subject: 'Tip' },
  { icon: '✍️', label: 'Give me a 5-step study plan for tomorrow’s test', subject: 'Plan' },
];

const SimpleMarkdown = ({ text }) => {
  // Very light markdown renderer: **bold**, lists, line breaks. No HTML injection allowed.
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((ln, i) => {
        if (!ln.trim()) return <div key={i} className="h-2" />;
        const orderedMatch = ln.match(/^(\d+)\.\s+(.*)$/);
        const bulletMatch = ln.match(/^[-*]\s+(.*)$/);
        const render = (s) => {
          const parts = s.split(/(\*\*[^*]+\*\*)/g);
          return parts.map((p, j) =>
            p.startsWith('**') && p.endsWith('**')
              ? <strong key={j} className="text-gray-900">{p.slice(2, -2)}</strong>
              : <span key={j}>{p}</span>
          );
        };
        if (orderedMatch) {
          return (
            <div key={i} className="flex gap-2">
              <span className="text-indigo-600 font-bold tabular-nums shrink-0">{orderedMatch[1]}.</span>
              <span className="flex-1">{render(orderedMatch[2])}</span>
            </div>
          );
        }
        if (bulletMatch) {
          return (
            <div key={i} className="flex gap-2 ml-2">
              <span className="text-indigo-500 shrink-0">•</span>
              <span className="flex-1">{render(bulletMatch[1])}</span>
            </div>
          );
        }
        return <p key={i}>{render(ln)}</p>;
      })}
    </div>
  );
};

const DoubtSolver = ({ onBack, initialContext }) => {
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => initialContext ? `Please explain this: ${initialContext.question || ''}` : '');
  const [sending, setSending] = useState(false);
  const [quota, setQuota] = useState({ used: 0, cap: 30, remaining: 30 });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [thinkingLabel, setThinkingLabel] = useState('Thinking…');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Rotate the "thinking" label while the LLM is generating so a 3-6s wait feels alive.
  useEffect(() => {
    if (!sending) { setThinkingLabel('Thinking…'); return; }
    const labels = [
      'Thinking…',
      'Reading your question…',
      'Working out the steps…',
      'Almost ready…',
      'Just a moment more…',
    ];
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(i + 1, labels.length - 1);
      setThinkingLabel(labels[i]);
    }, 1500);
    return () => clearInterval(t);
  }, [sending]);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/student/doubt-solver/sessions`);
      setSessions(data.sessions || []);
      setQuota(prev => data.quota || prev);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Auto-scroll on new message
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const openSession = async (sid) => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/student/doubt-solver/sessions/${sid}`);
      setActiveSessionId(sid);
      setMessages(data.messages || []);
    } catch {
      toast.error('Could not load chat');
    } finally { setLoadingHistory(false); }
  };

  const startNewChat = () => {
    setActiveSessionId(newSessionId());
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const removeSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await axios.delete(`${API}/student/doubt-solver/sessions/${sid}`);
      if (sid === activeSessionId) startNewChat();
      loadSessions();
    } catch { toast.error('Could not delete'); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    if (quota.remaining <= 0) {
      toast.error('Daily AI tutor quota reached — come back tomorrow!');
      return;
    }
    setInput('');
    // Optimistic user message
    setMessages(prev => [
      ...prev,
      { role: 'user', content: msg, created_at: new Date().toISOString() },
    ]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/student/doubt-solver/ask`, {
        session_id: activeSessionId,
        message: msg,
        context: messages.length === 0 ? initialContext : null,
      });
      setMessages(prev => [...prev, data.message]);
      setQuota({ used: data.used, cap: data.cap, remaining: data.remaining });
      loadSessions();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Something went wrong';
      toast.error(detail);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${detail}`, created_at: new Date().toISOString(), error: true },
      ]);
    } finally { setSending(false); }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row h-[calc(100vh-180px)] min-h-[520px]" data-testid="doubt-solver">
      {/* Left rail: sessions list */}
      <aside className="md:w-64 border-r bg-gray-50 flex flex-col">
        <div className="p-3 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <h3 className="font-bold flex items-center gap-2 text-sm"><Sparkles size={16} /> AI Tutor</h3>
          <p className="text-[11px] text-indigo-100 mt-1">{quota.remaining} of {quota.cap} questions left today</p>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden mt-2">
            <div className="h-full bg-amber-300" style={{ width: `${Math.min(100, (quota.used / quota.cap) * 100)}%` }} />
          </div>
        </div>
        <div className="p-2">
          <Button onClick={startNewChat} size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="new-chat-btn">
            <Plus size={14} className="mr-1" /> New chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {sessions.length === 0 ? (
            <p className="text-xs text-gray-400 text-center px-2 py-6">No past chats yet — ask your first doubt!</p>
          ) : sessions.map(s => (
            <button
              key={s.id}
              onClick={() => openSession(s.id)}
              className={`w-full text-left p-2.5 border-b hover:bg-indigo-50 group ${activeSessionId === s.id ? 'bg-indigo-100' : ''}`}
              data-testid={`session-${s.id}`}
            >
              <div className="flex justify-between items-start gap-1">
                <p className="text-xs font-medium text-gray-800 line-clamp-2 flex-1">{s.title || s.last_message_preview}</p>
                <span
                  onClick={(e) => removeSession(s.id, e)}
                  role="button"
                  tabIndex={0}
                  className="opacity-0 group-hover:opacity-100 text-red-500 shrink-0 hover:text-red-700 cursor-pointer"
                ><Trash2 size={12} /></span>
              </div>
              <p className="text-[10px] text-gray-400 mt-1">{new Date(s.last_message_at).toLocaleString()}</p>
            </button>
          ))}
        </div>
        {onBack && (
          <button onClick={onBack} className="p-2 text-xs text-gray-500 border-t hover:bg-gray-100 flex items-center gap-1">
            <ChevronLeft size={14} /> Back to dashboard
          </button>
        )}
      </aside>

      {/* Right: chat area */}
      <main className="flex-1 flex flex-col bg-white">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-r from-indigo-50 to-purple-50">
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm"><MessageSquare size={16} className="text-indigo-600" /> Ask anything — your AI tutor is here</h3>
            <p className="text-[11px] text-gray-500">Step-by-step explanations · SCERT/NCERT · Class 6–9</p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">{quota.remaining} left</Badge>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-white via-indigo-50/20 to-white">
          {loadingHistory ? (
            <div className="text-center py-12"><Loader2 className="mx-auto animate-spin text-indigo-500" /></div>
          ) : messages.length === 0 ? (
            <div className="max-w-xl mx-auto py-4">
              <div className="bg-white border-2 border-indigo-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Hi! I&apos;m your AI tutor 👋</p>
                    <p className="text-xs text-gray-500">Ask me ANY doubt from Math, Science, English, or Social Studies. I&apos;ll explain step by step.</p>
                  </div>
                </div>
                <div className="bg-indigo-50 rounded-lg p-3 mt-2">
                  <p className="text-xs font-semibold text-indigo-800 flex items-center gap-1 mb-2"><Lightbulb size={12} /> Try asking…</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {STARTERS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => send(s.label)}
                        className="text-left bg-white border border-indigo-200 rounded-lg p-2 text-xs hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex items-start gap-2"
                        data-testid={`starter-${i}`}
                      >
                        <span className="text-base shrink-0">{s.icon}</span>
                        <span className="flex-1 text-gray-700">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-[11px] text-gray-500">
                  <AlertCircle size={12} className="shrink-0 mt-0.5" />
                  <span>I won&apos;t write full assignments for you — but I&apos;ll explain anything you need to learn yourself.</span>
                </div>
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                  m.role === 'user'
                    ? 'bg-indigo-600 text-white rounded-tr-sm'
                    : m.error
                      ? 'bg-red-50 border border-red-200 text-red-800 rounded-tl-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm'
                }`}>
                  {m.role === 'assistant' && (
                    <div className="flex items-center gap-1.5 mb-1.5 text-[10px] text-indigo-600 font-semibold uppercase tracking-wide">
                      <Sparkles size={10} /> AI Tutor
                    </div>
                  )}
                  {m.role === 'user' ? (
                    <p className="whitespace-pre-wrap">{m.content}</p>
                  ) : (
                    <SimpleMarkdown text={m.content} />
                  )}
                </div>
              </div>
            ))
          )}
          {sending && (
            <div className="flex justify-start" data-testid="doubt-thinking">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm flex items-center gap-2 text-gray-600">
                <span className="flex gap-0.5" aria-hidden>
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span>{thinkingLabel}</span>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t p-3 bg-white">
          {quota.remaining <= 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
              <AlertCircle size={14} /> You&apos;ve used all {quota.cap} questions for today. Come back tomorrow!
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask anything — Math, Science, English, Social Studies…"
                rows={1}
                disabled={sending}
                className="flex-1 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-400 resize-none disabled:bg-gray-50"
                style={{ maxHeight: '120px' }}
                data-testid="doubt-input"
              />
              <Button
                onClick={() => send()}
                disabled={sending || !input.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 h-10 px-4 rounded-xl"
                data-testid="doubt-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          )}
          <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
            <BookOpen size={10} /> Press Enter to send · Shift+Enter for new line
          </p>
        </div>
      </main>
    </div>
  );
};

export default DoubtSolver;
